// Constants
import { DynamicFilterField, MyTasksFilterOptions } from "@ms/planner-models/lib/local/constants/FilterConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { DefaultViewTypeForPersonalViews, SubRouteKeys, ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { PlannerDefaultPlanId } from "@ms/uno-constants/lib/local/ServiceConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { DefaultMyTasksViewPivotType, MyTasksGroupingOptions, MyTasksPivotGroupingConfig, MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
// Models
import { EntityFilter } from "@ms/planner-models/lib/local/client/filter/Filter";
import { toSortByOption, toSortCriteria } from "@ms/planner-models/lib/local/utilities/MyTasksSortCriteria";
// Controls
import { format } from "@fluentui/utilities";
import { Table, TableFilled, Taskboard, TaskboardFilled } from "@ms/planner-generic-controls/lib/local/icons";
import { ViewNavigationBar } from "@ms/planner-presentational-controls/lib/local/viewNavigationBar/ViewNavigationBar";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { getMyTasksListViewId } from "@ms/planner-stores/lib/local/utilities/MyTasksFilterUtilities";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Resources
import { Strings as SharedStrings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { Strings } from "../../../../utilities/CultureUtilities";
// Utilities
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useInteractionTracker } from "@ms/planner-presentational-controls/lib/local/hooks/useInteractionTracker";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { getPrimaryViewContentWidth } from "@ms/planner-container-controls/lib/local/utilities/TaskEditorPanelLayoutUtilities";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
import { extractMyTasksRoutingDetailsFromSubRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { hasStoredGroupingPreference } from "@ms/uno-utilities/lib/local/ViewUtilities";
import * as React from "react";
// Constants for this component
const componentName = ViewName.MyTasksPage;
/**
 * Where in My Tasks the filter was applied, so one interaction name can still be read per view.
 * Qualified with the page because the same view types also exist under a plan.
 */ const getFilterInteractionEntryPoint = (viewType)=>{
    switch(viewType){
        case ViewType.Grid:
            return "tasksGrid";
        case ViewType.List:
            return "tasksList";
        default:
            return "tasksBoard";
    }
};
/**
 * Get the my tasks pivot items
 */ const getPivotItems = ()=>{
    return [
        {
            headerText: Strings.MyTasksPage_AllTasksPivotItemTitle,
            itemKey: MyTasksViewPivotType.All,
            tabElementId: ElementIds.MyTasksAllPivot,
            tooltipText: Strings.MyTasksPage_AllTasksPivotItemTooltip,
            dataTestId: "my-tasks-pivot-all"
        },
        {
            headerText: Strings.MyTasksPage_PrivateTasksPivotItemTitle,
            itemKey: MyTasksViewPivotType.PrivateTasks,
            tabElementId: ElementIds.MyTasksPrivatePivot,
            tooltipText: Strings.MyTasksPage_PrivateTasksPivotItemTooltip,
            dataTestId: "my-tasks-pivot-private"
        },
        {
            headerText: Strings.MyTasksPage_AssignedToMePivotItemTitle,
            itemKey: MyTasksViewPivotType.AssignedToMe,
            tabElementId: ElementIds.MyTasksAssignedToMePivot,
            tooltipText: Strings.MyTasksPage_AssignedToMePivotItemTooltip,
            dataTestId: "my-tasks-pivot-assigned-to-me"
        },
        {
            headerText: Strings.MyTasksPage_FlaggedEmailsPivotItemTitle,
            itemKey: MyTasksViewPivotType.FlaggedEmails,
            tabElementId: ElementIds.MyTasksFlaggedEmailsPivot,
            tooltipText: Strings.MyTasksPage_FlaggedEmailsPivotItemTooltip,
            dataTestId: "my-tasks-pivot-flagged-emails"
        }
    ];
};
/**
 * Get the new my tasks pivot items when EnableNewMyTasksExperience flight is on
 */ const getNewMyTasksPivotItems = ()=>{
    return [
        {
            headerText: Strings.MyTasksPage_PersonalPivotItemTitle,
            itemKey: MyTasksViewPivotType.PrivateTasks,
            tabElementId: ElementIds.MyTasksPrivatePivot,
            tooltipText: Strings.MyTasksPage_PersonalPivotItemTooltip,
            dataTestId: "my-tasks-pivot-private"
        },
        {
            headerText: Strings.MyTasksPage_StarredPivotItemTitle,
            itemKey: MyTasksViewPivotType.Important,
            tabElementId: ElementIds.MyTasksImportantPivot,
            tooltipText: Strings.MyTasksPage_StarredPivotItemTooltip,
            dataTestId: "my-tasks-pivot-starred"
        },
        {
            headerText: Strings.MyTasksPage_DueSoonPivotItemTitle,
            itemKey: MyTasksViewPivotType.DueSoon,
            tabElementId: ElementIds.MyTasksDueSoonPivot,
            tooltipText: Strings.MyTasksPage_DueSoonPivotItemTooltip,
            dataTestId: "my-tasks-pivot-due-soon"
        },
        {
            headerText: Strings.MyTasksPage_AssignedToMePivotItemTitle,
            itemKey: MyTasksViewPivotType.AssignedToMe,
            tabElementId: ElementIds.MyTasksAssignedToMePivot,
            tooltipText: Strings.MyTasksPage_AssignedToMePivotItemTooltip,
            dataTestId: "my-tasks-pivot-assigned-to-me"
        },
        {
            headerText: Strings.MyTasksPage_AllTasksPivotItemTitle,
            itemKey: MyTasksViewPivotType.All,
            tabElementId: ElementIds.MyTasksAllPivot,
            tooltipText: Strings.MyTasksPage_AllTasksPivotItemTooltip,
            dataTestId: "my-tasks-pivot-all"
        },
        {
            headerText: Strings.MyTasksPage_FlaggedEmailsPivotItemTitle,
            itemKey: MyTasksViewPivotType.FlaggedEmails,
            tabElementId: ElementIds.MyTasksFlaggedEmailsPivot,
            tooltipText: Strings.MyTasksPage_FlaggedEmailsPivotItemTooltip,
            dataTestId: "my-tasks-pivot-flagged-emails"
        }
    ];
};
/**
 * Resolves the current grouping for the GroupBy menu.
 * Distinguishes "user explicitly chose None" from "no stored preference" so
 * the menu shows the correct default per pivot when the user hasn't overridden it.
 */ function resolveCurrentGrouping(viewStore, viewId, currentPivot) {
    const pivotGroupingConfig = MyTasksPivotGroupingConfig[currentPivot];
    if (pivotGroupingConfig?.allowOverride === false) {
        return pivotGroupingConfig.defaultGrouping;
    }
    const storeGrouping = viewStore.getCurrentTaskGroupingState(viewId);
    const userGrouping = viewStore.getCurrentTaskGroupingState(viewId, true);
    const userHasStoredPreference = hasStoredGroupingPreference(storeGrouping, userGrouping);
    return userHasStoredPreference ? storeGrouping : pivotGroupingConfig?.defaultGrouping ?? TaskGroupingOption.Plan;
}
/**
 * Container component for My Tasks view navigation bar
 */ export const ViewNavigationBarContainer = ()=>{
    const [routeStore, viewStore, appContextStore, modalStore, taskStore, planStore, permissionsStore] = useStores([
        Store.Route,
        Store.View,
        Store.AppContext,
        Store.Modal,
        Store.Tasks,
        Store.Plan,
        Store.Permissions
    ]);
    const [routeActionCreator, viewTransformsActionCreator, userActionCreator, modalActionCreator] = useActionCreators([
        ActionCreator.Routing,
        ActionCreator.ViewTransforms,
        ActionCreator.User,
        ActionCreator.Modal
    ]);
    const { engagementLogger } = useLoggers();
    const { startInteraction } = useInteractionTracker();
    const configProvider = useConfigProvider();
    const primaryViewContentWidth = getPrimaryViewContentWidth({
        viewStore,
        modalStore,
        taskStore,
        planStore,
        permissionsStore
    }, configProvider);
    const todoSettings = appContextStore.getTodoSettings();
    const areToDoSettingsAvailable = todoSettings?.status?.state === StoreEntityState.Present;
    // Get current pivot from route
    const subRouteType = routeStore.getCurrentSubRouteType();
    const myTasksRoutingDetails = extractMyTasksRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), subRouteType);
    // Get current view type first so we can use it to determine the pivot default below
    const currentViewType = myTasksRoutingDetails?.viewType || DefaultViewTypeForPersonalViews;
    const isCanvasTaskGridEnabled = configProvider().flights.EnableCanvasTaskGrid;
    // Default to All in Grid view so new users can see private tasks they create.
    // Once the user explicitly selects a pivot (pushSubRoute), the RouteStore caches and persists that choice automatically.
    const defaultPivot = currentViewType === ViewType.Grid ? MyTasksViewPivotType.All : DefaultMyTasksViewPivotType;
    const currentPivot = myTasksRoutingDetails?.pivotType ?? defaultPivot;
    const isNewExperience = configProvider().flights.EnableNewMyTasksExperience && currentViewType === ViewType.List;
    // Dynamic filter (agent task preview) — fetch early to determine effective pivot
    const dynamicFilterViewId = {
        name: ViewName.MyTasksPage,
        partitionKey: currentPivot
    };
    const dynamicFilterEntityFilter = viewStore.getCurrentTaskFilter(dynamicFilterViewId);
    const isDynamicFilteringEnabled = isNewExperience && configProvider().flights.EnableCatalystForMyTasks && configProvider().flights.EnablePlannerAgentFiltering;
    const dynamicFilter = isDynamicFilteringEnabled ? dynamicFilterEntityFilter?.filters?.find((f)=>f.field === DynamicFilterField && f.label) ?? null : null;
    // When a dynamic filter is active, treat "All" as the effective pivot
    // so data operations (filters, grouping, sorting) use the "All" partition
    const isDynamicFilterActive = dynamicFilter?.active ?? false;
    const effectivePivotForData = isDynamicFilterActive ? MyTasksViewPivotType.All : currentPivot;
    const selectedPivotTabKey = isDynamicFilterActive ? null : currentPivot;
    const viewId = isNewExperience ? getMyTasksListViewId(currentPivot, dynamicFilterEntityFilter, isDynamicFilteringEnabled) : {
        name: ViewName.MyTasksPage,
        partitionKey: currentPivot
    };
    const filterKeyword = viewStore.getFilterKeyword(viewId);
    const currentTaskFilter = viewStore.getCurrentTaskFilter(viewId) ?? new EntityFilter([]);
    const filterCount = currentTaskFilter.filterCount ?? 0;
    const getUnfilteredTaskCount = ()=>{
        const currentUserId = appContextStore.getAppContext().userId ?? "";
        const flaggedEmailPlanId = planStore.getFlaggedEmailPlanId();
        const assignedTasksResult = taskStore.getTasksAssignedToUser(currentUserId);
        const privateTasksResult = taskStore.getTasksForPlan(PlannerDefaultPlanId);
        const flaggedTasksResult = flaggedEmailPlanId ? taskStore.getTasksForPlan(flaggedEmailPlanId) : null;
        const countIfPresent = (result)=>result.status.state === StoreEntityState.Present ? result.data.length : undefined;
        switch(effectivePivotForData){
            case MyTasksViewPivotType.PrivateTasks:
                return countIfPresent(privateTasksResult);
            case MyTasksViewPivotType.FlaggedEmails:
                return flaggedTasksResult ? countIfPresent(flaggedTasksResult) : 0;
            case MyTasksViewPivotType.AssignedToMe:
                return countIfPresent(assignedTasksResult);
            case MyTasksViewPivotType.All:
                {
                    const taskResults = [
                        assignedTasksResult,
                        privateTasksResult,
                        ...flaggedTasksResult ? [
                            flaggedTasksResult
                        ] : []
                    ];
                    if (taskResults.some((result)=>result.status.state !== StoreEntityState.Present)) {
                        return undefined;
                    }
                    return new Set(taskResults.flatMap((result)=>result.data.map((task)=>task.id))).size;
                }
            default:
                return undefined;
        }
    };
    const getKeywordFilterHelperText = ()=>{
        if (!configProvider().flights.EnableTaskFilterResultCount) {
            return undefined;
        }
        if (filterKeyword.trim().length === 0 && filterCount === 0) {
            return undefined;
        }
        const unfilteredTaskCount = getUnfilteredTaskCount();
        if (unfilteredTaskCount === undefined) {
            return undefined;
        }
        const filteredTaskCount = taskStore.getFilteredTaskCount(viewId);
        if (filteredTaskCount === 0) {
            return SharedStrings.TaskFiltering_FilterNoResultsFound;
        }
        const resultCountString = filteredTaskCount === 1 ? SharedStrings.TaskFiltering_FilterResultOfTotal : SharedStrings.TaskFiltering_FilterResultsOfTotal;
        return format(resultCountString, filteredTaskCount, unfilteredTaskCount);
    };
    const isGroupByDisabled = isNewExperience && MyTasksPivotGroupingConfig[effectivePivotForData]?.allowOverride === false;
    const currentTaskGroupingForMenu = resolveCurrentGrouping(viewStore, viewId, effectivePivotForData);
    const pivotItems = isNewExperience ? getNewMyTasksPivotItems().filter((item)=>configProvider().flights.EnableStarredExperience || item.itemKey !== MyTasksViewPivotType.Important) : getPivotItems();
    // In the new experience, view tabs are hidden entirely
    const showViewTabs = !isNewExperience;
    const viewTabsProps = showViewTabs ? {
        itemProps: [
            {
                headerText: SharedStrings.SharedComponents_PlanGridPageName,
                itemIcon: /*#__PURE__*/ React.createElement(Table, null),
                itemIconFilled: /*#__PURE__*/ React.createElement(TableFilled, null),
                itemKey: ViewType.Grid,
                tooltipText: SharedStrings.SharedComponents_PlanGridTooltip,
                tabElementId: ElementIds.GridViewPivot,
                dataTestId: "my-tasks-view-grid"
            },
            {
                headerText: SharedStrings.SharedComponents_PlanTaskboardPageName,
                itemIcon: /*#__PURE__*/ React.createElement(Taskboard, null),
                itemIconFilled: /*#__PURE__*/ React.createElement(TaskboardFilled, null),
                itemKey: ViewType.Board,
                tooltipText: SharedStrings.SharedComponents_PlanTaskboardTooltip,
                tabElementId: ElementIds.BoardViewPivot,
                dataTestId: "my-tasks-view-board"
            }
        ],
        selectedTabKey: currentViewType,
        onSelectTabItem: (viewType)=>{
            engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.MyTasksSwitchViewType, {
                viewType,
                isNewExperience: String(isNewExperience)
            });
            // Close the task editor if it's the topmost modal before switching views
            if (modalStore.getCurrentModalData().modalType === ModalType.TaskEditor) {
                modalActionCreator.closeModal();
            }
            // When switching to Grid, don't carry over AssignedToMe — default to All so private tasks are visible.
            // Other pivots (All, PrivateTasks, FlaggedEmails) carry over as-is.
            const pivot = viewType === ViewType.Grid && currentPivot === MyTasksViewPivotType.AssignedToMe ? MyTasksViewPivotType.All : currentPivot;
            routeActionCreator.pushSubRoute([
                pivot,
                SubRouteKeys.View,
                viewType
            ]);
        }
    } : undefined;
    return /*#__PURE__*/ React.createElement(ViewNavigationBar, {
        viewTabsProps: viewTabsProps,
        viewTabsPosition: isNewExperience ? "right" : "left",
        pivotTabsProps: {
            // In the new experience, always show pivot tabs (they don't depend on ToDo settings).
            // In the legacy experience, hide pivot tabs when ToDo settings aren't available (e.g. guest users).
            itemProps: isNewExperience || areToDoSettingsAvailable ? pivotItems : [],
            selectedTabKey: selectedPivotTabKey,
            onSelectTabItem: (pivot)=>{
                engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.MyTasksSwitchPivot, {
                    pivot,
                    isNewExperience: String(isNewExperience)
                });
                // Close the task editor if it's the topmost modal before switching pivots
                if (modalStore.getCurrentModalData().modalType === ModalType.TaskEditor) {
                    modalActionCreator.closeModal();
                }
                // Reset dynamic filter when other pivots are selected
                if (dynamicFilter?.active && dynamicFilterEntityFilter) {
                    // Current expected behavior for the my tasks agent preview is to simply treat it as a pivot filter, not a toggle filter applied to a selected pivot.
                    // So when the user switches to another pivot, we remove the dynamic filter.
                    viewTransformsActionCreator.setTaskFilter(dynamicFilterViewId, dynamicFilterEntityFilter.setFilterActive(DynamicFilterField, !dynamicFilter.active));
                }
                routeActionCreator.pushSubRoute([
                    pivot,
                    SubRouteKeys.View,
                    currentViewType
                ]);
            },
            overflowButtonAriaLabel: Strings.MyTasksPage_OverflowButtonAriaLabel,
            overflowButtonTooltip: Strings.MyTasksPage_OverflowButtonTooltip
        },
        taskFilterProps: isNewExperience ? undefined : {
            currentTaskFilter,
            setTaskFilter: (newFilter, isClearFilters, filterRemoved, announcementTitleStrParams)=>{
                // Same FilterByMenu interaction as the plan views, armed at this pivot's
                // viewId. Each view settles it itself once it has drawn the filtered result.
                startInteraction({
                    name: "FilterByMenu",
                    key: isClearFilters ? null : newFilter
                }, viewId, getFilterInteractionEntryPoint(currentViewType));
                viewTransformsActionCreator.setTaskFilter(viewId, newFilter, isClearFilters, filterRemoved, announcementTitleStrParams);
            },
            taskFilterOptions: MyTasksFilterOptions,
            getUserPhoto: ()=>null,
            onRenderPersona: (user)=>{
                userActionCreator.fetchUsers([
                    user.id
                ]);
            },
            enableBlockedStatus: configProvider().flights.EnableBlockedStatus,
            enableCustomDateRange: configProvider().flights.EnableCustomDateRangeFilter
        },
        groupByMenuProps: isNewExperience ? {
            taskGroupingOptions: isGroupByDisabled ? [] : MyTasksGroupingOptions,
            currentTaskGrouping: currentTaskGroupingForMenu,
            onGroupByChange: (taskGroupState)=>{
                engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.MyTasksChangeGrouping, {
                    groupBy: taskGroupState,
                    isNewExperience: String(isNewExperience)
                });
                startInteraction({
                    name: "GroupBy",
                    key: taskGroupState
                }, viewId, "tasksList");
                viewTransformsActionCreator.setTaskGroupingState(viewId, taskGroupState);
            },
            disabled: isGroupByDisabled,
            disabledTooltip: isGroupByDisabled ? SharedStrings.PlanHeader_GroupByDisabledTooltip : undefined,
            useNoneGroupingLabel: true
        } : undefined,
        sortByMenuProps: isNewExperience ? {
            currentSortBy: toSortByOption(viewStore.getSortCriteria(viewId)),
            currentGroupBy: currentTaskGroupingForMenu,
            onSortByChange: (option)=>{
                engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.MyTasksChangeSort, {
                    sort: option,
                    isNewExperience: String(isNewExperience)
                });
                viewTransformsActionCreator.setSortCriteria(viewId, toSortCriteria(option));
            }
        } : undefined,
        keywordFilterProps: isNewExperience ? undefined : {
            key: viewId.partitionKey,
            placeholder: Strings.MyTasksActions_FilterByKeyword,
            filterInput: filterKeyword,
            ...!isCanvasTaskGridEnabled && {
                helperText: getKeywordFilterHelperText()
            },
            onFilterInputUpdated: (keyword)=>{
                engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.FilterByKeyword, {
                    isNewExperience: String(isNewExperience)
                });
                viewTransformsActionCreator.setTaskFilterKeyword(viewId, keyword);
            }
        },
        dynamicFilterPillProps: dynamicFilter?.label ? {
            filterLabel: dynamicFilter.label,
            isActive: dynamicFilter.active,
            onToggle: ()=>{
                // Expected behavior is for the dynamic filter to behave as a pivot filter, and not a toggle filter. Similar to how the "Assigned to me" pivot filter works.
                if (!dynamicFilter.active) {
                    engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.DynamicFilterToggled, {
                        isActive: String(!dynamicFilter.active)
                    });
                    if (dynamicFilterEntityFilter) {
                        viewTransformsActionCreator.setTaskFilter(dynamicFilterViewId, dynamicFilterEntityFilter.setFilterActive(DynamicFilterField, !dynamicFilter.active));
                        if (effectivePivotForData !== MyTasksViewPivotType.All) {
                            routeActionCreator.pushSubRoute([
                                MyTasksViewPivotType.All,
                                SubRouteKeys.View,
                                currentViewType
                            ]);
                        }
                    }
                }
            },
            onDismiss: ()=>{
                engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.DynamicFilterRemoved);
                viewTransformsActionCreator.removeDynamicFilter(dynamicFilterViewId, true);
            }
        } : undefined,
        width: primaryViewContentWidth
    });
};
